<template>
  <tile class="opc-zmone">
    <zm-alert v-if="licenseAlertDesc && !notification" type="warning" show-icon :closable="false" class="opc-base-alert">
      <p class="opc-base-alert__message" v-html="licenseAlertDesc" @mouseover="() => $tracking.buyInteraction('banner - product level', trackingPlanName + ' - ' + licenseAlertDesc + ' - impression')" />
    </zm-alert>
    <zm-alert v-if="notification" type="warning" show-icon :closable="false" class="opc-base-alert">
      <p class="opc-base-alert__message" v-html="notification"  @mouseover="() => $tracking.buyInteraction('banner - product level', trackingPlanName + ' - ' + notification + ' - impression')" />
    </zm-alert>
    <foldable v-model="isExpanded" :title="trackingPlanName" :isRemoveWrapAriaLabelledby="true">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img class="opc-zm-base-card__logo" :src="logo" alt="Zoom One" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h1 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i class="zm-icon-success main-icon"></i>
          </h1>
          <div class="opc-zm-base-card__view-detail-button">
            <zm-button class="opc-zm-base-card__link" type="link" @click="handlePlanDetailsClick"
              v-if="isExpanded && showPlanDetail">
              {{ $t('billing.plan_details') }}
            </zm-button>
          </div>
          <div class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" v-if="!isExpanded && formPlan">
            {{
              $t('billing.zone.desc1', [
                form.selectedNumber,
                $money(formPlan ? formPlan.amount : 0),
                $t(`billing.common.per_billing_cycle_user.${form.billingCycle}`),
              ])
            }}
          </div>
        </div>
      </div>
      <div class="opc-zmone__main">
        <zm-form ref="zmoneForm" label-position="top" class="opc-config-form">
          <zm-form-item>
            <p class="opc-zmone__form-label" slot="label">
              {{ $t('billing.zone.select_license') }}
              <zm-tooltip v-model="attendeeInfoTooltip" class="item"
                aria-label="Learn more about Select the number of users"
                :content="$t('billing.zone.attendee_info', [attendee])" placement="top">
                <i class="zm-icon-info-outline main-icon" role="button"></i>
              </zm-tooltip>
            </p>
            <license-tip v-if="isNormalCase" :type="form.category" :number="form.selectedNumber" :additionalSpans='additionalSpans'>
              <zm-button v-if="showExploreLink" class="opc-zmone__link" type="link"
                @click="handleExploreOtherPlansClick">{{ $t('billing.zone.explore_other_plans') }}</zm-button>
            </license-tip>
            <InputNumberWithPopover class="opc-zmone__quantity-selector" v-model="form.selectedNumber" size="large"
              controls-position="both" :min="minHost" :max="maxHost" @change="updatePlanLicense"
              :decrease-tips="decreaseTips" :label="trackingPlanName + ' Select the number of users'" />
            <zm-alert class="reason-tips" v-if="licenseTips" type="info" :closable="false">
              <i class="zm-icon-info" style="color: #9191A4;"></i>
              <p v-html="licenseTips"></p>
            </zm-alert>
          </zm-form-item>
          <zm-form-item :label="$t('billing.select_billing_cycle')">
            <cycle-radio-group v-model="form.billingCycle" :plans="currentPlans" :action="switchBillingCycle"
              aria-label="Zoom One Billing Cycle" class="opc-card-radio">
              <zm-tooltip v-for="plan in currentPlans" :key="plan.billing_cycle"
                :disabled="!(typeof billingCycleDisabled(plan) === 'string')">
                <div slot="content" v-html="billingCycleDisabled(plan) || ''" />
                <zm-radio :label="plan.billing_cycle" :disabled="!!billingCycleDisabled(plan)"
                  class="opc-zmone__radio">
                  <p class="opc-zmone__radio-title text-capitalize">{{ $t(`billing.billing_cycle.${plan.billing_cycle}`)
                  }}
                    <save-count
                      v-if="plan.billing_cycle === BILLING_CYCLE.ANNUAL"
                      :disabled="!!billingCycleDisabled(plan)"
                      :annual-price="cyclePrice.annualPrice"
                      :monthly-price="cyclePrice.monthlyPrice"
                    />
                  </p>
                  <p class="opc-zmone__radio-price" tabindex="-1">
                    <span>
                      {{ $money(plan.amount, { forceDiff: true }) }}
                    </span>
                    <span class="opc-zmone__radio-price-unit">{{ getPlanUnitWithCycle(plan.billing_cycle) }}</span>
                  </p>
                </zm-radio>
              </zm-tooltip>
            </cycle-radio-group>
          </zm-form-item>
          <preview-payment />
        </zm-form>
        <div class="opc-zm-base-card__main__footer" v-if="couldBeRemovedAsAddonCard">
          <zm-button type="link" ghost @click="removePlanByKey"
            :aria-label="'remove ' + getCategoryName(form.category) + ' plan'">{{ $t('billing.common.remove')
            }}</zm-button>
        </div>
      </div>
    </foldable>
  </tile>
</template>
<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import LicenseTip from '../license-tip.vue';
import {
  BILLING_CYCLE,
  limitBillingCycleWithBaseZoneTypes,
  planTypes,
  BASE_PLAN_TYPES, BillingCycleEnum,
} from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import logo from '@/assets/plans/icon_ZM.svg';
import { DEFAULT_MEETING_PLAN_CAPACITY, getCategoryNameI18nKey, isBizPlusContext, isZoomMeetingAsAddonCard } from '@/plans/ZOOM_MEETING/common/utils';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import FullComparisonDialog from '@/plans/ZOOM_MEETING/dialog/full-comparison-dialog';
import PlanDetails from '@/plans/ZOOM_MEETING/dialog/plan-details';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { debounce, get, groupBy, throttle } from 'lodash';
import { isEmptyVal } from '@/utils';
import { getTitleNameI18nKey } from '../common/utils';
import saveMixins from '../save-mixins';
import { plansof } from '@/utils/chain';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import { getOnlinePlanName } from '@/common/product';
import _ from "lodash";
import { getPlanUnitWithCycle } from '@/common/product-unit';
// import { PLAN_TYPES_WITH_MULTI_BASE_PLANS } from '@/plans/ROOM_CONNECTOR/common/utils';
import autoCycle from '../mixin/auto-cycle.js';
import PreviewPayment from '@/views/checkout/payment/preview-payment';

export default {
  key: planTypes[planTypes.ZOOM_MEETING],
  components: {
    PreviewPayment,
    Foldable,
    Tile,
    InputNumberWithPopover,
    LicenseTip,
  },
  mixins: [plansMixin, saveMixins, autoCycle],
  data() {
    return {
      logo,
      attendeeInfoTooltip: false,
      isExpanded: true,
      form: {...DEFAULT_MEETING_PLAN_CAPACITY},
      BILLING_CYCLE: BILLING_CYCLE,
      fullComparisonDialog: new ServiceConfirm(FullComparisonDialog, this.$store),
      planDetailsDialog: new ServiceConfirm(PlanDetails, this.$store),
      showPlanDetail: false,
      showCategoryDesc: false,
      isInitUpdateForm: false,
      notification: '',
      isBizPlusContext,
    };
  },
  created() {
    this.updateForm(true);
    this.$context.$on('notify-meeting', message => {
      this.notification = message;
    });
  },
  beforeDestroy() {
    this.$context.$off('notify-meeting');
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters(['getSelectedPlans', 'getUserCurrentSubscriptions', 'enableStandaloneCycleLimit', 'limitLicenseWithBaseZoneTypes', 'getCurrentBasePlanInfo', 'plansofBizBundle']),
    isNormalCase() {
      return true;
    },
    additionalSpans() {
      return {};
    },
    showExploreLink() {
      return false;
    },
    title() {
      return this.$t(getTitleNameI18nKey(this.form?.category))
    },
    zmAsBasePlan() {
      const key = this.$options.key;
      const planType = planTypes[key];
      return this.getCurrentBasePlanInfo.planType === planType;
    },
    zmIsLastBasePlan() {
      const key = this.$options.key;
      const planType = planTypes[key];
      let baseTypes = plansof(this.getSelectedPlans).type(...BASE_PLAN_TYPES).get().map(x => x.type);
      // 1. is last base plan, e.g. CRC could have more than 1 bas plan
      // 2. zm must as base plan, e.g. Scheduler no need zoom meeting as base plan
      return (baseTypes.length === 1 && baseTypes[0] === planType) && this.zmAsBasePlan
    },
    couldBeRemovedAsAddonCard() {
      return isZoomMeetingAsAddonCard() && !this.zmIsLastBasePlan || isBizPlusContext;
    },
    zonePlans() {
      return this.planMapFlat.filter((item) => item.type === planTypes.ZOOM_MEETING);
    },
    zonePlanGroupByCategory() {
      return groupBy(this.zonePlans, 'category');
    },
    currentPlans() {
      return this.zonePlanGroupByCategory[this.form.category];
    },
    annualPriceM() {
      const monthPlan = this.currentPlans.find((item) => item.billing_cycle === BILLING_CYCLE.MONTHLY);
      return monthPlan ? monthPlan.amount * 12 : 0;
    },
    annalPriceSave() {
      const annualPlan = this.currentPlans.find((item) => item.billing_cycle === BILLING_CYCLE.ANNUAL);
      if (this.annualPriceM && annualPlan) {
        return this.annualPriceM - annualPlan.amount;
      }
      return 0;
    },
    saveLabel() {
      return {
        [BILLING_CYCLE.MONTHLY]: '',
        [BILLING_CYCLE.ANNUAL]: this.annalPriceSave > 0
          ? `${this.$money(this.annalPriceSave)} ${this.$t('billing.annual_save')}`
          : '',
      };
    },
    // current selected meeting plan
    formPlan() {
      return this.zonePlanGroupByCategory[this.form.category].find(
        (item) => item.billing_cycle === this.form.billingCycle,
      );
    },
    totalCoast() {
      if (this.formPlan) {
        return this.formPlan.amount * this.form.selectedNumber;
      }
      return 0;
    },
    exceedBaseLicensePlans() {
      return this.getUserCurrentSubscriptions.filter(item => (
        _.includes(this.limitLicenseWithBaseZoneTypes, item.type) &&
        item.cartInfo.selected_number >= this.form.selectedNumber
      ));
    },
    annualAddonPlans() {
      const newLimit = this.enableStandaloneCycleLimit ? [planTypes.ZOOM_SCHEDULER, planTypes.ZOOM_WHITEBOARD] : [];
      return this.getUserCurrentSubscriptions.filter(item => (
        limitBillingCycleWithBaseZoneTypes.concat(newLimit).includes(item.type) &&
        item.billing_cycle === BILLING_CYCLE.ANNUAL
      ));
    },
    maxAddonLicense() {
      return this.exceedBaseLicensePlans.reduce((license, plan) => Math.max(plan.cartInfo.selected_number, license), 0);
    },
    minHost() {
      if(isBizPlusContext) return 1;
      const planMin = this.formPlan ? this.formPlan.min : 1;
      if (this.$context.is.add2cart) {
        const { bundleLicenses } = this.plansofBizBundle;
        if (bundleLicenses > 0) {
          return Math.max(Math.min(planMin, this.maxAddonLicense - bundleLicenses), 1);
        }
      }
      return Math.max(planMin, this.maxAddonLicense);
    },
    maxHost() {
      return this.formPlan ? this.formPlan.max : 99;
    },
    decreaseTips() {
      if(isBizPlusContext) return '';
      const planGroupByType = groupBy(this.exceedBaseLicensePlans, 'type');
      const typeNames = Object.keys(planGroupByType).map(type => {
        const typeNameKey = get(planGroupByType[type], '0.typeNameKey', '');
        return this.$t(typeNameKey);
      }).filter(Boolean);
      if (this.$context.is.add2cart) {
        const { bundleLicenses } = this.plansofBizBundle;
        if (bundleLicenses > 0) {
          return this.form.selectedNumber <= this.maxAddonLicense - bundleLicenses ? this.$t('billing.licences_limit_tips', [typeNames.join(','), this.$t('billing.planform.title.zoom_meeting_zoomonenew')]) : '';
        }
      }
      return this.form.selectedNumber <= this.maxAddonLicense ? this.$t('billing.licences_limit_tips', [typeNames.join(','), this.$t('billing.planform.title.zoom_meeting_zoomonenew')]) : '';
    },
    attendee() {
      return get(this.currentPlans, "[0].participants", 0);
    },
    trackingPlanName() {
      return `zoom one ${this.form?.category}`;
    },
    licenseAlertDesc() {
      return ""
    },
    mx_defLicenseAlertDesc() {
      const configPlan = plansof(this.getSelectedPlans).type(this.$context.type).get(0);
      const meetingPlan = plansof(this.getSelectedPlans).type(planTypes.ZOOM_MEETING).get(0);
      const { licenseDependBasePlanTypeList = [] } = configPlan;
      const isRequired = licenseDependBasePlanTypeList.includes(planTypes.ZOOM_MEETING);
      if (isRequired) {
        if(configPlan.isPPA || [planTypes.ROOM_CONNECTOR].includes(this.$context.type)) {
          return this.$t("billing.common.consumption_equal_to_base", [
            this.$t(getPlanTypeName(this.$context.type)),
            getOnlinePlanName(meetingPlan),
          ])
        } else {
          const maximumLicensePlan = _.chain(this.getSelectedPlans)
            .filter((p) => _.includes(p?.licenseDependBasePlanTypeList, planTypes.ZOOM_MEETING))
            .orderBy((p) => p?.cartInfo?.selected_number, "desc")
            .get(0)
            .value();

          const typeName = this.$t(getPlanTypeName(maximumLicensePlan?.type || this.$context.type));

          return this.$t("billing.common.license_equal_to_base", [
            getOnlinePlanName(meetingPlan),
            typeName,
          ])
        }
      }
      // For some plans like CRC, only need at least 1 license of base plan
      const { dependBasePlanTypeList = [] } = configPlan;
      const isJustBasePlanRequired = dependBasePlanTypeList.includes(planTypes.ZOOM_MEETING);
      if (isJustBasePlanRequired) {
        // if(PLAN_TYPES_WITH_MULTI_BASE_PLANS.includes(this.$context.type)) {
        return this.$t("billing.common.consumption_equal_to_base", [
          this.$t(getPlanTypeName(this.$context.type)),
          getOnlinePlanName(meetingPlan),
        ])
        // }
      }
      return "";
    },
    cartMeetingPlan() {
      return this.getSelectedPlans.find((item) => item.type === planTypes.ZOOM_MEETING);
    },
    licenseTips() {
      return '';
    }
  },
  mounted() {
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    ...mapActions(['switchBillingCycleAction']),
    ...mapMutations(['updateSelectPlan', 'savePersist']),
    // sync shopping cart plan to local form state
    updateForm(isInitUpdateForm = false) {
      const shoppingCartPlan = this.cartMeetingPlan;
      if (shoppingCartPlan) {
        this.form.category = shoppingCartPlan.category;
        this.form.billingCycle = shoppingCartPlan.billing_cycle;
        this.form.selectedNumber = shoppingCartPlan.cartInfo.selected_number;
      }

      this.isInitUpdateForm = isInitUpdateForm;
    },
    async switchBillingCycle(oldPlan, newPlan) {
      if (!this.formPlan) {
        return;
      }

      const cartInfo = { selected_number: this.form.selectedNumber };
      await this.switchBillingCycleAction([oldPlan?.plan_code, {
        ...newPlan,
        cartInfo,
      }]);

      this.updateForm();
    },
    updatePlanLicense: throttle(function (v) {
      this.$nextTick(async () => {
        if (!this.formPlan) {
          return;
        }

        if (isEmptyVal(v)) {
          this.form.selectedNumber = Math.max(this.formPlan?.cartInfo?.selected_number ?? 0, this.minHost);
        }

        this.updateSelectPlan({
          ...this.formPlan,
          cartInfo: {
            selected_number: this.form.selectedNumber,
          },
        });

        this.updateForm();
      });
    }, 200),
    async handlePlanDetailsClick() {
      this.$tracking.buyInteraction('plan details selected', this.trackingPlanName);
      await this.planDetailsDialog.confirm({ planCategory: this.formPlan.category });
      await this.showFullComparisonDialog();
    },
    async handleExploreOtherPlansClick() {
      this.$tracking.buyInteraction('explore other plans', `${this.trackingPlanName} - explore other plans`);
      await this.showFullComparisonDialog();
    },
    async showFullComparisonDialog() {
      this.$tracking.buyInteraction('product details', 'full plan comparison - impression');
      await this.fullComparisonDialog.confirm();
      this.$nextTick(() => this.updateForm());
    },
    getCategoryName(category) {
      return this.$t(getCategoryNameI18nKey(category));
    },
    getBillingCycleDisabledDesc(billingCycle) {
      // from auto-cycle.js
      if (this.autoDisabledCycle(billingCycle)) {
        return this.isAutoDisabledCycleDesc;
      }
      if (this.form.billingCycle === billingCycle && billingCycle === BILLING_CYCLE.ANNUAL) {
        return false;
      }
      const exceedAddonPlans = this.getExceedCycleAddonPlans(BillingCycleEnum[billingCycle]);
      const planGroupByType = groupBy(exceedAddonPlans, 'type');
      const typeNames = Object.keys(planGroupByType).map(type => {
        const typeNameKey = get(planGroupByType[type], '0.typeNameKey', '');
        return this.$t(typeNameKey);
      }).filter(Boolean);
      if (exceedAddonPlans.length) {
        if(billingCycle === BILLING_CYCLE.MONTHLY){
          return this.$t('billing.planform.monthly_base_disable_tooltip', [typeNames.join(','), this.$t('billing.planform.title.zoom_meeting_zoomonenew')]);
        }else {
          return true;
        }
      }
      return false;
    },
    billingCycleDisabled({ billing_cycle, disabled }) {
      return this.getBillingCycleDisabledDesc(billing_cycle) || disabled === true;
    },
    getPlanUnitWithCycle(cycle) {
      return getPlanUnitWithCycle(cycle);
    },
    getExceedCycleAddonPlans(cycleValue) {
      const newLimit = this.enableStandaloneCycleLimit ? [planTypes.ZOOM_SCHEDULER, planTypes.ZOOM_WHITEBOARD] : [];
      return this.getUserCurrentSubscriptions.filter(item => (
        limitBillingCycleWithBaseZoneTypes.concat(newLimit).includes(item.type) &&
        item.cycleValue > cycleValue
      ));
    },
    collapseCard() {
      this.isExpanded = false;
    },
  },
  watch: {
    attendeeInfoTooltip: {
      handler(val) {
        val && this.$tracking.buyInteraction(
          `${this.trackingPlanName} options`,
          'number of users - tooltip'
        );
      }
    },
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `${this.trackingPlanName} - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
    'form.billingCycle': {
      handler: debounce(function (val) {
        !this.isInitUpdateForm && this.$tracking.buyInteraction(
          'billing cycle',
          `${this.trackingPlanName} - toggled billed ${val}`,
        );
      }, 500),
    },
    'form.selectedNumber': {
      handler: debounce(function (newLicense, oldLicense) {
        if (this.formPlan) {
          // event tracking
          !this.isInitUpdateForm && this.$tracking.buyInteraction('number of hosts',
            `${this.trackingPlanName} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
        }
      }, 500),
    },
    cartMeetingPlan: {
      deep: true,
      handler() {
        this.updateForm();
      },
    }
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

.opc-zmone {
  .main-icon {
    color: $theme-color;
  }

  &__link.zm-button--link {
    margin-left: 5px;
    font-weight: 700 !important;
    color: $theme-color;
    padding: 0;
  }

  &__quantity-selector {
    margin-top: 12px;
  }

  &__desc {
    padding-bottom: 20px;
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    color: #00053d;
  }

  &__main {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #dfdfe4;
  }

  &__desc {
    position: relative;
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
    color: #515079;
    padding: 0;
    margin: 0;

    &.is-warning {
      padding: 8px 12px 8px 40px;
      background: #FEF7D9;
      border-radius: 8px;

      &::before {
        content: '';
        background-image: url('../../../assets/Alert.svg');
        width: 16px;
        height: 16px;
        background-repeat: no-repeat;
        background-size: cover;
        position: absolute;
        top: calc(50% - 8px);
        left: 12px;
      }
    }
  }

  &__form-label {
    margin: 0;
    padding: 0;
    color: #00031F;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 27px;
  }

  .opc-card-radio {
    margin-top: 8px;

    .opc-zmone__radio {
      width: 100%;

      .zm-radio__wrap {
        width: 100%;
      }
    }
  }

  .opc-card-radio &__radio {
    .zm-radio__label--inner {
      width: 100%;
      // min-height: 136px;
      padding: 16px;
    }

    .zm-radio__label {
      &.is-disabled {
        .opc-zmone__radio-title, .opc-zmone__radio-price, .opc-zmone__radio-price-unit {
          color: #9191A4 !important;
        }
      }
    }

    &-title {
      color: #00053D;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;
      margin: 0;
      display: flex;
      flex-wrap: wrap;

      .opc-price-save-count {
        margin-left: 4px;
      }
    }

    &-save-label {
      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 21px;
      color: #4a7d5c;
      min-width: 1px;
      min-height: 21px;
    }

    &-price {
      color: #00053D;
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 33px;

      &-unit {
        color: #666487;
        font-size: 12px;
        font-style: normal;
        font-weight: 450;
        line-height: 120%;
      }

      &-delete {
        font-weight: 500;
        font-size: 14px;
        line-height: 150%;
        text-decoration-line: line-through;
        color: #515079;
      }
    }
  }
}

.opc-base-alert {
  &.zm-alert {
    background: #F7F7F8;
    margin-bottom: 24px !important;

    .zm-alert__icon {
      color: #666487 !important;
    }
  }

  &__message {
    font-family: Almaden Sans;
    font-size: 14px;
    font-weight: 450;
    line-height: 21px;
    letter-spacing: 0em;
    text-align: left;
  }
}
</style>
